import { inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  CanMatchFn,
  UrlSegment,
  Route,
  Router,
} from '@angular/router';
import {
  CustomNavigationService,
  NotificationService,
  SessionService,
} from '@kimbocare/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserSubscription } from '../models/Subscription';
import { SubscriptionService } from '../rest/usecases/subscription.service';

export function HasNotSubsGuardWithRedirection(): CanActivateFn {
  return (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree => {
    const authService: SessionService = inject(SessionService);

    const navService: CustomNavigationService = inject(CustomNavigationService);
    const subs = authService.getSubscription() as UserSubscription;
    if (
      !subs?.product ||
      (subs?.product &&
        subs.product.ends_at &&
        new Date(subs.product.ends_at) <= new Date())
    ) {
      return true;
    }
    navService.goTo('/dashboard');

    return false;
  };
}

export function HasNotSubsGuard(): CanActivateFn {
  return (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree => {
    const authService: SessionService = inject(SessionService);
    const subs = authService.getSubscription() as UserSubscription;
    if (
      !subs?.product ||
      (subs?.product &&
        subs.product.ends_at &&
        new Date(subs.product.ends_at) <= new Date())
    ) {
      return true;
    }

    return false;
  };
}

/* export const HasSubsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean> => {
  const authService: SessionService = inject(SessionService);
  const notificationService: NotificationService = inject(NotificationService);
  const translateService: TranslateService = inject(TranslateService);
  const navService: CustomNavigationService = inject(CustomNavigationService);
  const subs = authService.getSubscription() as UserSubscription;
  const subscription = inject(SubscriptionService);
  const initialCheck = checkSub(
    subs,
    notificationService,
    navService,
    translateService,
  );

  console.log('initial check', initialCheck);

  if (initialCheck) {
    return of(true);
  }

  return subscription.getUserSubscription().pipe(
    tap((userSub) => authService.storeSubscription(userSub)),
    map((userSub: UserSubscription) =>
      checkSub(userSub, notificationService, navService, translateService),
    ),
    catchError(() => {
      // Handle API errors gracefully
      return of(false);
    }),
  );
}; */

export const hasSubscriptionMatch: CanMatchFn = (): Observable<
  boolean | UrlTree
> => {
  const auth = inject(SessionService);
  const subSvc = inject(SubscriptionService);
  const notif = inject(NotificationService);
  const translate = inject(TranslateService);
  const router = inject(Router);

  const localSub = auth.getSubscription();
  const localCheck = checkSubLocal(localSub);

  // ✅ Certitude locale → OK
  if (localCheck === true) {
    return of(true);
  }

  // ❌ Expiré localement → redirect
  if (localCheck === false) {
    return of(redirectToSubs(router, notif, translate));
  }

  // ❓ Inconnu → API
  return subSvc.getUserSubscription().pipe(
    tap((sub) => auth.storeSubscription(sub)),
    map((sub) => {
      const apiCheck = checkSubLocal(sub);
      return apiCheck === true
        ? true
        : redirectToSubs(router, notif, translate);
    }),
  );
};

function redirectToSubs(
  router: Router,
  notif: NotificationService,
  translate: TranslateService,
): UrlTree {
  notif.warning(translate.instant('plans.subs_end_or_not_present'), 10000);
  return router.createUrlTree(['/subscriptions']);
}

type SubCheckResult = true | false | 'unknown';

function checkSubLocal(subs?: UserSubscription): SubCheckResult {
  if (!subs) return 'unknown';

  if (
    subs.user &&
    subs.product?.ends_at &&
    new Date(subs.product.ends_at) > new Date()
  ) {
    return true;
  }

  if (subs?.product && new Date(subs.product.ends_at) <= new Date())
    return false;

  return 'unknown';
}
